import { stores, getStoreById } from './stores';
import { cities, getCityById } from './cities';
import { categories, getWeightedRandomCategory } from './categories';
import { products, getProductsByCategory, getRandomProduct } from './products';
import { customers, getRandomCustomer } from './customers';

// Constants
const ORDER_STATUSES = ['Delivered', 'Shipped', 'Processing', 'Cancelled', 'Returned'];
const PAYMENT_METHODS = ['Online', 'COD'];
const COUPON_CODES = ['FIRST50', 'SAVE10', 'CHICKEN20', 'WEEKEND15', 'LOYALTY25', null, null, null, null]; // null = no coupon

// Date helpers
function addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

function formatDate(date) {
    return date.toISOString().split('T')[0];
}

function isWeekend(date) {
    const day = date.getDay();
    return day === 0 || day === 6;
}

// Random helpers with seeding for consistency
function seededRandom(seed) {
    const x = Math.sin(seed++) * 10000;
    return x - Math.floor(x);
}

// Generate order items
function generateOrderItems(orderSeed) {
    const itemCount = Math.floor(seededRandom(orderSeed) * 4) + 1; // 1-4 items
    const items = [];

    for (let i = 0; i < itemCount; i++) {
        const category = getWeightedRandomCategory();
        const categoryProducts = getProductsByCategory(category.id);
        const product = categoryProducts[Math.floor(seededRandom(orderSeed + i * 100) * categoryProducts.length)];

        if (product) {
            const quantity = Math.floor(seededRandom(orderSeed + i * 200) * 3) + 1;
            items.push({
                productId: product.id,
                productName: product.name,
                categoryId: category.id,
                categoryName: category.name,
                price: product.price,
                quantity,
                total: product.price * quantity,
            });
        }
    }

    return items;
}

// Calculate discount
function calculateDiscount(subtotal, couponCode, orderSeed) {
    if (!couponCode) return 0;

    const discountMap = {
        'FIRST50': Math.min(50, subtotal * 0.1),
        'SAVE10': subtotal * 0.10,
        'CHICKEN20': subtotal * 0.20,
        'WEEKEND15': subtotal * 0.15,
        'LOYALTY25': subtotal * 0.25,
    };

    return Math.round(discountMap[couponCode] || 0);
}

// Generate 6 months of orders
function generateOrders() {
    const orders = [];
    const today = new Date();
    const startDate = new Date(today);
    startDate.setMonth(startDate.getMonth() - 6);

    let orderId = 1;
    let currentDate = new Date(startDate);

    while (currentDate <= today) {
        // Base daily orders (varies by day)
        let baseOrders = 40 + Math.floor(Math.random() * 20);

        // Weekend boost (15-25% more orders)
        if (isWeekend(currentDate)) {
            baseOrders = Math.floor(baseOrders * (1.15 + Math.random() * 0.10));
        }

        // Occasional discount days (higher volume)
        const isDiscountDay = Math.random() < 0.1; // 10% chance
        if (isDiscountDay) {
            baseOrders = Math.floor(baseOrders * 1.3);
        }

        // Generate orders for this day
        for (let i = 0; i < baseOrders; i++) {
            const orderSeed = orderId * 31 + currentDate.getTime();
            const store = stores[Math.floor(seededRandom(orderSeed) * stores.length)];
            const city = getCityById(store.cityId);
            const customer = customers[Math.floor(seededRandom(orderSeed + 1) * customers.length)];

            // Generate items
            const items = generateOrderItems(orderSeed);
            const subtotal = items.reduce((sum, item) => sum + item.total, 0);

            // Coupon (more likely on discount days)
            const couponCode = isDiscountDay
                ? COUPON_CODES[Math.floor(seededRandom(orderSeed + 2) * 5)]
                : COUPON_CODES[Math.floor(seededRandom(orderSeed + 2) * COUPON_CODES.length)];

            const discount = calculateDiscount(subtotal, couponCode, orderSeed);
            const deliveryFee = subtotal > 500 ? 0 : 30;
            const total = subtotal - discount + deliveryFee;

            // Payment method (influenced by city COD preference and pincode)
            const codChance = city ? city.codPreference : 0.3;
            const paymentMethod = seededRandom(orderSeed + 3) < codChance ? 'COD' : 'Online';

            // Order status (most are delivered for older orders)
            const daysSinceOrder = Math.floor((today - currentDate) / (1000 * 60 * 60 * 24));
            let status;
            if (daysSinceOrder > 3) {
                const statusRand = seededRandom(orderSeed + 4);
                if (statusRand < 0.92) status = 'Delivered';
                else if (statusRand < 0.96) status = 'Cancelled';
                else status = 'Returned';
            } else if (daysSinceOrder > 1) {
                const statusRand = seededRandom(orderSeed + 4);
                if (statusRand < 0.7) status = 'Delivered';
                else if (statusRand < 0.9) status = 'Shipped';
                else status = 'Processing';
            } else {
                status = seededRandom(orderSeed + 4) < 0.3 ? 'Shipped' : 'Processing';
            }

            // Order time (peak hours: 10-13, 18-21)
            let hour;
            const hourRand = seededRandom(orderSeed + 5);
            if (hourRand < 0.35) hour = 10 + Math.floor(seededRandom(orderSeed + 6) * 3);
            else if (hourRand < 0.7) hour = 18 + Math.floor(seededRandom(orderSeed + 6) * 3);
            else hour = 8 + Math.floor(seededRandom(orderSeed + 6) * 12);

            const orderTime = `${String(hour).padStart(2, '0')}:${String(Math.floor(seededRandom(orderSeed + 7) * 60)).padStart(2, '0')}`;

            orders.push({
                id: `ORD-${String(orderId).padStart(6, '0')}`,
                customerId: customer.id,
                customerName: customer.name,
                storeId: store.id,
                storeName: store.name,
                cityId: store.cityId,
                pincode: store.pincode,
                date: formatDate(currentDate),
                time: orderTime,
                items,
                itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
                subtotal,
                discount,
                couponCode,
                deliveryFee,
                total,
                paymentMethod,
                status,
                isNewCustomer: customer.isNew,
            });

            orderId++;
        }

        currentDate = addDays(currentDate, 1);
    }

    return orders;
}

export const orders = generateOrders();

// Aggregation helpers
export function getOrdersByDateRange(startDate, endDate) {
    return orders.filter(order => order.date >= startDate && order.date <= endDate);
}

export function getOrdersByCity(cityId) {
    return orders.filter(order => order.cityId === cityId);
}

export function getOrdersByStore(storeId) {
    return orders.filter(order => order.storeId === storeId);
}

export function getOrdersByStatus(status) {
    return orders.filter(order => order.status === status);
}

export function getTotalRevenue(orderList = orders) {
    return orderList.reduce((sum, order) => sum + order.total, 0);
}

export function getTotalOrders(orderList = orders) {
    return orderList.length;
}

export function getAverageOrderValue(orderList = orders) {
    if (orderList.length === 0) return 0;
    return Math.round(getTotalRevenue(orderList) / orderList.length);
}

// Statistics
export const orderStats = {
    get totalOrders() { return orders.length; },
    get totalRevenue() { return getTotalRevenue(); },
    get avgOrderValue() { return getAverageOrderValue(); },
    get deliveredOrders() { return orders.filter(o => o.status === 'Delivered').length; },
    get cancelledOrders() { return orders.filter(o => o.status === 'Cancelled').length; },
    get onlinePayments() { return orders.filter(o => o.paymentMethod === 'Online').length; },
    get codPayments() { return orders.filter(o => o.paymentMethod === 'COD').length; },
};

export default orders;
